import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Popover, PopoverModule } from 'primeng/popover';
import {
    NotificacionesInvitacionService,
    NotificacionInvitacion
} from '@/shared/services/notificaciones-invitacion.service';

@Component({
    selector: 'app-notificaciones',
    standalone: true,
    imports: [CommonModule, RouterModule, OverlayBadgeModule, PopoverModule],
    template: `
        <button
            type="button"
            class="layout-topbar-action"
            aria-label="Notificaciones"
            (click)="op.toggle($event)"
        >
            @if ((sinLeer$ | async); as n) {
                <p-overlaybadge [value]="n > 9 ? '9+' : n.toString()" severity="danger">
                    <i class="pi pi-bell"></i>
                </p-overlaybadge>
            } @else {
                <i class="pi pi-bell"></i>
            }
        </button>

        <p-popover #op (onShow)="onAbrir()" [style]="{ width: '360px' }">
            <div class="flex flex-col max-h-[26rem]">
                <div class="flex items-center justify-between px-3 py-2 border-b border-surface-200 dark:border-surface-700">
                    <span class="font-semibold text-surface-900 dark:text-surface-0">Notificaciones</span>
                    @if ((notificaciones$ | async)?.length) {
                        <button
                            type="button"
                            class="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            (click)="limpiar()"
                        >
                            Limpiar
                        </button>
                    }
                </div>

                <div class="overflow-y-auto">
                    @for (n of (notificaciones$ | async); track n.id) {
                        <button
                            type="button"
                            class="w-full text-left px-3 py-3 border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                            [ngClass]="{ 'bg-primary-50 dark:bg-primary-900/20': !n.leida }"
                            (click)="irADetalle(n)"
                        >
                            <div class="flex items-start gap-2">
                                <span
                                    class="mt-1 inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300"
                                >
                                    <i class="pi pi-user-plus text-sm"></i>
                                </span>
                                <div class="min-w-0 flex-1">
                                    <p class="text-sm text-surface-800 dark:text-surface-100">
                                        Nueva invitación por confirmar de
                                        <span class="font-semibold">{{ nombreCompleto(n) }}</span>
                                    </p>
                                    <p class="truncate text-xs text-surface-500 dark:text-surface-400">
                                        {{ n.identificacion }} · {{ n.email }}
                                    </p>
                                    <p class="mt-0.5 text-[11px] text-surface-400 dark:text-surface-500">
                                        {{ hace(n.recibida_en || n.created_at) }}
                                    </p>
                                </div>
                            </div>
                        </button>
                    } @empty {
                        <div class="px-3 py-10 text-center text-sm text-surface-400 dark:text-surface-500">
                            <i class="pi pi-inbox block text-2xl mb-2"></i>
                            Sin notificaciones
                        </div>
                    }
                </div>

                @if ((notificaciones$ | async)?.length) {
                    <div class="px-3 py-2 border-t border-surface-200 dark:border-surface-700 text-center">
                        <a
                            routerLink="/administracion/invitaciones"
                            class="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                            (click)="op.hide()"
                        >
                            Ver todas las invitaciones
                        </a>
                    </div>
                }
            </div>
        </p-popover>
    `
})
export class AppNotificaciones implements OnInit {
    @ViewChild('op') op!: Popover;

    readonly notificaciones$;
    readonly sinLeer$;

    constructor(
        private readonly notificacionesService: NotificacionesInvitacionService,
        private readonly router: Router
    ) {
        this.notificaciones$ = this.notificacionesService.notificaciones$;
        this.sinLeer$ = this.notificacionesService.sinLeer$;
    }

    ngOnInit(): void {
        this.notificacionesService.iniciar();
    }

    onAbrir(): void {
        this.notificacionesService.marcarTodasLeidas();
    }

    limpiar(): void {
        this.notificacionesService.limpiar();
    }

    irADetalle(n: NotificacionInvitacion): void {
        this.op.hide();
        this.router.navigate(['/administracion/invitaciones']);
    }

    nombreCompleto(n: NotificacionInvitacion): string {
        return [n.nombres, n.apellidos].filter(Boolean).join(' ').trim() || 'Nuevo usuario';
    }

    hace(iso: string | undefined): string {
        if (!iso) return '';
        const fecha = new Date(iso);
        if (Number.isNaN(fecha.getTime())) return '';

        const segundos = Math.floor((Date.now() - fecha.getTime()) / 1000);
        if (segundos < 60) return 'hace un momento';
        const minutos = Math.floor(segundos / 60);
        if (minutos < 60) return `hace ${minutos} min`;
        const horas = Math.floor(minutos / 60);
        if (horas < 24) return `hace ${horas} h`;
        const dias = Math.floor(horas / 24);
        if (dias < 30) return `hace ${dias} d`;
        return fecha.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
    }
}
